/**
 * LogoMark - Eclipse Accounting symbol-only logo
 *
 * The crescent eclipse mark with Solar gold crescent and Umbra disc.
 * Use this for compact spaces like sidebars, favicons, and app icons.
 *
 * @param size - Pixel height (width auto-scales); preset sizes: 16, 20, 24, 32, 48, 64, 96, 128
 * @param className - Additional Tailwind classes
 */

import React from 'react';
import { cn } from '@/lib/utils';

interface LogoMarkProps {
  size?: 16 | 20 | 24 | 32 | 48 | 64 | 96 | 128 | number;
  className?: string;
  colorScheme?: 'auto' | 'dark';
  'aria-label'?: string;
  'aria-hidden'?: boolean;
}

export function LogoMark(...args: Parameters<typeof useLogoMarkView>) {
  return useLogoMarkView(...args);
}

export function useLogoMarkView({
  size = 24,
  className,
  colorScheme = 'auto',
  'aria-label': ariaLabel = 'Eclipse crescent logomark',
  'aria-hidden': ariaHidden = false,
}: LogoMarkProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 2000 2000"
      width={size}
      height={size}
      className={cn('relative flex-shrink-0', className)}
      role={ariaHidden ? undefined : 'img'}
      aria-label={ariaHidden ? undefined : ariaLabel}
      aria-hidden={ariaHidden || undefined}
    >
      <path
        d="M1258.24 335.37C1625.01 320.66 1934.37 605.85 1949.48 972.61C1964.59 1339.37 1679.73 1649.04 1312.98 1664.54C945.68 1680.07 635.43 1394.68 620.31 1027.36C605.18 660.04 890.90 350.10 1258.24 335.37Z"
        fill="var(--umbra, #0E0E10)"
        stroke="var(--accent-50, #FFB000)"
        strokeWidth="10"
        strokeOpacity={colorScheme === 'dark' ? 0.3 : undefined}
        className={
          colorScheme === 'dark'
            ? undefined
            : 'dark:stroke-primary/30 stroke-transparent'
        }
      />

      <path
        fill="var(--accent-50, #FFB000)"
        d="M689.96 335.38C709.49 334.40 727.35 335.3 746.82 336.16C733.8 345.32 721.42 356.76 709.66 367.50C674.65 399.24 642.38 433.86 613.18 471.01C472.73 649.44 408.99 876.39 435.98 1101.85C457.4 1280.09 534.41 1447.06 656.09 1579.05C684.07 1609.96 714.57 1638.48 747.29 1664.32C652.02 1666.91 560.12 1655.85 471.37 1618.93C407.76 1596.72 332.81 1549.29 282.14 1504.99C146.88 1389.07 63.61 1223.86 50.90 1046.18C39.44 868.49 98.80 693.50 215.99 559.46C340.79 417.31 503.34 347.50 689.96 335.38Z"
      />
    </svg>
  );
}
